// Modal listing top match suggestions for an unassigned housing request.
import { useEffect, useState } from "react";
import api from "@/lib/api";

const TYPE_LABELS = { habitant: "Habitant", hotel: "Hôtel", gite: "Gîte", congregation: "Congrégation" };

export default function HousingMatchModal({ requestId, locale = "fr", onClose, onAssigned }) {
  const isFr = locale === "fr";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    api.get(`/admin/housing/match/${requestId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [requestId]);

  const assign = async (offerId) => {
    setAssigning(offerId);
    try {
      await api.post(`/admin/housing/requests/${requestId}/assign`, { offer_id: offerId });
      onAssigned();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setAssigning(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: "rgba(20,16,12,0.7)" }} data-testid="housing-match-modal">
      <div className="admin-card max-w-2xl w-full max-h-[85vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl">
            ◯ {isFr ? "Suggestions d'affectation" : "Match suggestions"}
          </h3>
          <button className="btn btn-outline btn-sm" onClick={onClose} data-testid="match-modal-close">✕</button>
        </div>

        {!data && !err && <p style={{ color: "var(--muted)" }}>{isFr ? "Chargement…" : "Loading…"}</p>}
        {err && <p style={{ color: "var(--cinnabar)" }}>{err}</p>}

        {data && (
          <>
            <div className="p-3" style={{ backgroundColor: "var(--surface-2)", border: "1px solid var(--border)" }}>
              <p className="text-sm">
                <strong>{data.request.guest_name}</strong> · {data.request.nb_people} {isFr ? "personne(s)" : "people"}
              </p>
              <p className="text-xs" style={{ color: "var(--muted)" }}>
                {data.request.arrival || "?"} → {data.request.departure || "?"}
                {data.request.preference && data.request.preference !== "indifferent" && (
                  <span> · {isFr ? "préférence" : "preference"} : {TYPE_LABELS[data.request.preference] || data.request.preference}</span>
                )}
              </p>
            </div>

            {data.suggestions.length === 0 ? (
              <p className="text-center italic" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucune offre disponible avec capacité suffisante." : "No offer with enough capacity."}
              </p>
            ) : (
              <div className="space-y-2">
                {data.suggestions.map((s, i) => (
                  <div key={s.offer.id} className="p-3 flex items-start gap-3"
                       style={{ border: "1px solid var(--border)", borderLeft: `4px solid ${i === 0 ? "var(--moss)" : "var(--gold)"}` }}
                       data-testid={`match-suggestion-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-display text-base">{s.offer.host_name}</span>
                        <span className="text-xs" style={{ color: "var(--muted)" }}>· {s.offer.city}</span>
                        <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                              style={{ backgroundColor: "var(--gold)", color: "white", letterSpacing: "0.15em" }}>
                          {TYPE_LABELS[s.offer.type] || s.offer.type}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-1">
                        {s.reasons.map((r, j) => (
                          <span key={j} className="text-[0.65rem] px-2 py-0.5 rounded-sm"
                                style={{ backgroundColor: "var(--surface-2)", color: "var(--ink-2)" }}>
                            ✓ {r}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs" style={{ color: "var(--muted)" }}>
                        {isFr ? "Capacité restante" : "Remaining capacity"} : {s.offer.remaining_capacity ?? s.offer.capacity}/{s.offer.capacity}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-2xl" style={{ color: i === 0 ? "var(--moss)" : "var(--gold)" }}>
                        {s.score}
                      </div>
                      <div className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.15em" }}>
                        SCORE
                      </div>
                      <button className="btn btn-primary btn-sm mt-2"
                              disabled={assigning}
                              onClick={() => assign(s.offer.id)}
                              data-testid={`match-assign-${i}`}>
                        {assigning === s.offer.id ? "…" : (isFr ? "Affecter" : "Assign")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
